import React, { useState, useEffect, useCallback } from 'react';
import { X, Printer } from 'lucide-react';

// Printable conduct report: attendance summary + detail, behavior records.
// Separately gated from grades (academic.reports.conduct).
const ConductReportModal = ({ apiCall, year, student, onClose, showError }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiCall(`/academic/reports/conduct-card?studentId=${encodeURIComponent(student.StudentID)}&academicYear=${year}`));
    } catch (err) { showError(err.message); }
  }, [apiCall, student.StudentID, year, showError]);

  useEffect(() => { load(); }, [load]);

  const buildHTML = () => {
    if (!data) return '';
    const { student: stu, attendance, attendanceDetail, behavior } = data;
    const TD = 'border:1px solid #666;padding:4px 8px;';

    return `
      <div style="text-align:center;margin-bottom:10px;">
        <h1 style="font-size:17px;margin:0;">Collège des Apôtres — Jounieh</h1>
        <h2 style="font-size:13px;margin:3px 0;color:#333;">Assiduité &amp; Comportement · ${year}</h2>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:10px;">
        <tr>
          <td style="${TD}"><b>Élève:</b> ${stu.FirstName} ${stu.LastName}</td>
          <td style="${TD}"><b>Classe:</b> ${stu.Class} ${stu.Section || ''}</td>
          <td style="${TD}"><b>ID:</b> ${stu.StudentID}</td>
        </tr>
      </table>

      <h3 style="margin:10px 0 4px 0;font-size:13px;">Assiduité</h3>
      <table style="width:50%;border-collapse:collapse;font-size:11px;margin-bottom:6px;">
        <tr style="background:#f0f0f0;font-weight:bold;">
          <td style="${TD}">Absences</td><td style="${TD}">Retards</td><td style="${TD}">Excusées</td>
        </tr>
        <tr>
          <td style="${TD}text-align:center;">${attendance.Absences}</td>
          <td style="${TD}text-align:center;">${attendance.Lates}</td>
          <td style="${TD}text-align:center;">${attendance.Excused}</td>
        </tr>
      </table>
      ${attendanceDetail.length ? `
        <table style="width:100%;border-collapse:collapse;font-size:10px;">
          <tr style="background:#f0f0f0;font-weight:bold;">
            <td style="${TD}">Date</td><td style="${TD}">Statut</td><td style="${TD}">Note</td>
          </tr>
          ${attendanceDetail.map(a => `<tr>
            <td style="${TD}">${new Date(a.AttendanceDate).toLocaleDateString()}</td>
            <td style="${TD}">${a.Status}</td>
            <td style="${TD}">${a.Notes || ''}</td>
          </tr>`).join('')}
        </table>` : '<p style="font-size:11px;color:#666;">Aucune absence enregistrée.</p>'}

      <h3 style="margin:12px 0 4px 0;font-size:13px;">Comportement</h3>
      ${behavior.length ? `
        <table style="width:100%;border-collapse:collapse;font-size:10px;">
          <tr style="background:#f0f0f0;font-weight:bold;">
            <td style="${TD}">Date</td><td style="${TD}">Type</td><td style="${TD}">Catégorie</td>
            <td style="${TD}">Description</td><td style="${TD}">Action</td>
          </tr>
          ${behavior.map(b => `<tr>
            <td style="${TD}white-space:nowrap;">${new Date(b.RecordDate).toLocaleDateString()}</td>
            <td style="${TD}text-align:center;">${b.RecordType === 'Merit' ? '✔ Mérite' : '✖ Incident'}</td>
            <td style="${TD}">${b.Category || ''}</td>
            <td style="${TD}">${b.Description || ''}</td>
            <td style="${TD}">${b.ActionTaken || ''}</td>
          </tr>`).join('')}
        </table>` : '<p style="font-size:11px;color:#666;">Aucun enregistrement.</p>'}

      <div style="display:flex;justify-content:space-between;margin-top:30px;font-size:11px;">
        <span>La direction: ................</span>
        <span>Les parents: ................</span>
      </div>`;
  };

  const handlePrint = () => {
    const w = window.open('', '', 'width=900,height=700');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Conduite - ${student.FirstName} ${student.LastName}</title>
      <style>@page{size:A4 portrait;margin:1.5cm}*{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,sans-serif;color:#111;padding:4px}</style>
      </head><body>${buildHTML()}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Attendance &amp; Behavior — {student.FirstName} {student.LastName} · {year}</h3>
          <div className="flex gap-2">
            <button onClick={handlePrint} disabled={!data}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              <Printer size={15} /> Print
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {!data ? (
            <p className="text-center text-gray-500 py-8">Building conduct report…</p>
          ) : (
            <div className="border-2 border-gray-300 rounded-lg p-5 bg-white shadow-inner"
                 dangerouslySetInnerHTML={{ __html: buildHTML() }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConductReportModal;
